import { useMemo, useState } from 'react';
import {
  Typography,
  Box,
  Grid,
  styled,
  Paper,
  useTheme,
  Chip,
} from '@mui/material';
import automotorImg from '../assets/imponible-automotor.png';
import cementerioImg from '../assets/imponible-cementerio.png';
import comercioImg from '../assets/imponible-comercio.png';
import contribuyenteImg from '../assets/imponible-contribuyente.png';
import inmobiliarioImg from '../assets/imponible-inmobiliario.png';
import Flecha from '../assets/icons/flecha-icon';
import { Vencimiento } from '../features/expirations/expirationsAPI';
import { useNavigate } from 'react-router-dom';
import { setCurrent } from '../features/payments/paymentsSlice';
import { store } from '../store';
import { createComprobante } from '../features/comprobantes/comprobantesSlice';
import { LoadingButton as LoadingButtonOriginal } from '@mui/lab';
import { getLabelTipoComprobante } from '../utils';

interface WithRow {
  row: number;
}

interface ExpirationProps extends WithRow {
  expiration: Vencimiento;
}

const ImageWrapper = styled(Paper, {
  shouldForwardProp: prop => prop !== 'row',
})<WithRow>(({ theme, row }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  float: 'left',
  marginRight: '1rem',

  [theme.breakpoints.up('md')]: {
    float: 'none',
    marginRight: 0,
    gridRow: row,
    gridColumn: 1,
  },
}));

const ReferenciaWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'row',
})<WithRow>(({ theme, row }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',

  [theme.breakpoints.up('md')]: {
    gridRow: row,
    gridColumn: 2,
  },
}));

const DescriptionWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'row',
})<WithRow>(({ theme, row }) => ({
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'center',

  [theme.breakpoints.up('md')]: {
    gridRow: row,
    gridColumn: 3,
  },
}));


const DebtWrapper = styled(Grid, {
  shouldForwardProp: prop => prop !== 'row',
})<WithRow>(({ theme, row }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'start',
  width: '100%',
  marginTop: '.5rem',

  [theme.breakpoints.up('md')]: {
    width: 'auto',
    marginTop: 0,
    marginBottom: 0,
    gridRow: row,
    gridColumn: 4,
  },
}));

const ExpirationWrapper = styled(Grid, {
  shouldForwardProp: prop => prop !== 'row',
})<WithRow>(({ theme, row }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'start',
  width: '100%',
  marginTop: '.5rem',

  [theme.breakpoints.up('md')]: {
    marginTop: 0,
    gridRow: row,
    gridColumn: 5,
  },
}));

const ActionsWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'row',
})<WithRow>(({ theme, row }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'wrap',
  marginTop: '.5rem',

  [theme.breakpoints.up('md')]: {
    marginTop: 0,
    gridRow: row,
    gridColumn: 6,
  },
}));

const Image = styled('img')(() => ({
  height: 'auto',
  minWidth: '1rem',
  maxWidth: '100%',
}));

const LoadingButton = styled(LoadingButtonOriginal)(() => ({
  '&.Mui-disabled': {
    backgroundColor: '#fafafa',
  },
}));

interface StatusChipProps {
  vencido: boolean;
}

const StatusChip = styled(Chip, {
  shouldForwardProp: prop => prop !== 'vencido',
})<StatusChipProps>(({ theme, vencido = false }) => ({
  borderRadius: '2px',
  fontWeight: 600,
  backgroundColor: vencido ? '#F3DAE1' : '#F1FFF6',
  color: vencido ? '#E73F6B' : '#64CD87',
  width: '100%',

  [theme.breakpoints.up('md')]: {
    width: 'auto',
  },
}));

export default function ExpirationItem(props: ExpirationProps) {
  const theme = useTheme();
  const navigate = useNavigate();

  const { expiration } = props;
  const { referencia, tipo } = props.expiration.imponible;
  const { descripcion, numero } = props.expiration.recurso;

  const imagen = useMemo(() => {
    switch (tipo) {
      case 'ROD': return automotorImg;
      case 'CEM': return cementerioImg;
      case 'INM': return inmobiliarioImg;
      case 'COM': return comercioImg;
      default: return contribuyenteImg;
    }
  }, [tipo]);

  const [loading, setLoading] = useState(false);

  const handlePlan = () => {
    setLoading(true);

    navigate('/planes', {
      state: {
        selectedCuotas: expiration.tipo == 'no_vencido' ? [expiration.cuota] : expiration.cuotas,
        resourceNumber: props.expiration.recurso.numero,
        imponible: props.expiration.imponible,
      },
    });
  };

  const handlePayment = () => {
    if (!props.expiration.imponible || !props.expiration.recurso) {
      return;
    }

    setLoading(true);

    store.dispatch(setCurrent({
      selectedCuotas: expiration.tipo == 'no_vencido' ? [expiration.cuota] : expiration.cuotas,
      selectedResource: numero,
      imponible: props.expiration.imponible,
    }));

    store.dispatch(createComprobante({
      tipo: props.expiration.imponible.tipo,
      imponible: props.expiration.imponible.imponible,
      recurso: numero,
      periods: (expiration.tipo == 'no_vencido' ? [expiration.cuota] : expiration.cuotas)
        .map(c => c.cuota.padStart(2, '0') + '/' + c.anio),
    })).unwrap().then(() => {
      navigate('/pagar');
    });
  };

  return (
    <>
      <ImageWrapper row={props.row}>
        <Box sx={{ backgroundColor: '#F5F8FA', padding: '.4rem' }}>
          <Image src={imagen} />
        </Box>
      </ImageWrapper>

      <ReferenciaWrapper row={props.row}>
        <Typography sx={{ fontWeight: 'bold', lineHeight: '1' }}>
          {referencia}
        </Typography>
        <Typography sx={{ color: theme.palette.grey[400] }}>
          {getLabelTipoComprobante(tipo)}
        </Typography>
      </ReferenciaWrapper>

      <DescriptionWrapper row={props.row}>
        <Typography variant="body2" sx={{
          fontSize: '.8rem',
          color: theme.palette.grey[700],
          lineHeight: '1rem',
        }}>
          {descripcion}
        </Typography>
      </DescriptionWrapper>

      <DebtWrapper row={props.row}>
        {
          expiration.tipo !== 'vencido'
            ? <>
              <Typography variant="body1" sx={{ color: theme.palette.grey[400] }}>
                Vencimiento: {expiration.vencimiento}
              </Typography>
              <Typography sx={{ color: '#64CD87' }}>
                <strong>Monto: ${expiration.monto_original.toFixed(2)}</strong>
              </Typography>
            </>
            : <Typography sx={{ color: '#E73F6B' }}>
              <strong>
                Deuda: ${(expiration.monto_original + expiration.intereses + expiration.multa).toFixed(2)}
              </strong>
            </Typography>
        }
      </DebtWrapper>

      <ExpirationWrapper row={props.row}>
        <StatusChip
          label={expiration.tipo === 'vencido' ? 'Vencido' : 'Vigente'}
          vencido={expiration.tipo === 'vencido'}
          size={theme.breakpoints.up('md') ? 'small' : 'medium'}
        />
      </ExpirationWrapper>

      <ActionsWrapper row={props.row}>
        <LoadingButton
          variant="outlined"
          onClick={handlePlan}
          disabled={!expiration.planes || expiration.tipo === 'no_vencido'}
          loading={loading}
          sx={{
            fontSize: '.7rem',
            padding: '3px 10px',
            whiteSpace: 'nowrap',
            margin: '.25rem',
          }}
        >
          CREAR PLAN
        </LoadingButton>

        <LoadingButton
          variant="contained"
          onClick={handlePayment}
          sx={{
            padding: '5px 10px',
            margin: '.25rem',
            color: 'white',
            fontSize: '.7rem',
          }}
          loading={loading}
          endIcon={loading ? null : <Flecha color={'white'} w={'11'} h={'9'} />}
        >
          PAGAR
        </LoadingButton>
      </ActionsWrapper>
    </>
  );
}
